import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import Logo from "./Logo";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const HERO_IMG =
  "https://images.unsplash.com/photo-1552072232-059f1a5d76c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4Mzg5OTUyM3ww&ixlib=rb-4.1.0&q=85";

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-sp-dark" data-testid="gate-loading">
    <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
  </div>
);

const GateScreen = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Sign in failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-sp-dark p-6" data-testid="site-gate">
      {/* Blurred brand backdrop */}
      <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25 scale-110 blur-md" />
      <div className="absolute inset-0 bg-gradient-to-b from-sp-dark/80 via-sp-dark/70 to-sp-dark sp-grain" />

      {/* Popup sign-in card */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="rounded-2xl border border-white/12 bg-white/[0.06] backdrop-blur-2xl shadow-[0_24px_80px_rgba(0,0,0,0.45)] p-8 md:p-10">
          <div className="flex justify-center">
            <Logo variant="dark" withText={false} />
          </div>

          <div className="mt-8 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[0.62rem] uppercase tracking-[0.22em] text-white/60">
              <Lock className="w-3 h-3" /> By Invitation
            </span>
            <h1 className="mt-6 font-serif text-3xl text-white">Sunly Prime</h1>
            <p className="mt-3 font-sans text-sm leading-relaxed text-white/55">
              This is a private site. Please sign in with the credentials provided
              to you to continue.
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="gate-form">
            <div>
              <Label htmlFor="gate-email" className="text-white/70">Email / User ID</Label>
              <Input
                id="gate-email"
                type="text"
                data-testid="gate-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/30"
                placeholder="Your email or user ID"
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="gate-password" className="text-white/70">Password</Label>
              <Input
                id="gate-password"
                type="password"
                data-testid="gate-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/30"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <p data-testid="gate-error" className="text-sm text-red-300 bg-red-500/10 border border-red-400/20 rounded px-4 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="gate-submit"
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-medium text-sp-deep hover:bg-sky-100 transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Verifying…" : "Sign In"}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.75} />
            </button>
          </form>

          <p className="mt-7 text-center font-sans text-xs text-white/40">
            Interested in access?{" "}
            <a href="mailto:admin@sunlyprime.asia" data-testid="gate-contact" className="text-white/70 underline underline-offset-4">
              admin@sunlyprime.asia
            </a>
          </p>
        </div>

        <p className="mt-6 text-center font-mono text-[0.62rem] uppercase tracking-[0.2em] text-white/30">
          Sunly Prime &middot; An affiliate of the Sunly Properties group
        </p>
      </motion.div>
    </div>
  );
};

export const SiteGate = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) return <LoadingScreen />;
  if (!user) return <GateScreen />;
  return children;
};

export default SiteGate;
